import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaSearch, FaTimesCircle } from 'react-icons/fa';
import { Container, Row, Col } from 'react-bootstrap';

import { cloudKitchenCategories } from '../data/categories';
import { isCloudKitchenOpen } from '../utils/isCloudKitchenOpen';
import { fetchCloudKitchens } from '../api/apiService';

// Ensure you save the CSS above and import it here
import '../styles/CloudKitchens.css'; 

const CloudKitchens = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [cloudKitchens, setCloudKitchens] = useState(location.state?.cloudKitchens || []);
    const [filtered, setFiltered] = useState(location.state?.cloudKitchens || []);
    const [activeCat, setActiveCat] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [showPopup, setShowPopup] = useState(true);
    const [loading, setLoading] = useState(!location.state?.cloudKitchens);

    const scrollRef = useRef(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);
    const listRef = useRef(null);

    // --- 1. Fetch Logic ---
    useEffect(() => {
        if (!location.state?.cloudKitchens) {
            const loadCloudKitchens = async () => {
                setLoading(true);
                try {
                    const response = await fetchCloudKitchens();
                    const arr = Array.isArray(response.data.cloudKitchens) ? response.data.cloudKitchens : [];
                    setCloudKitchens(arr);
                    setFiltered(arr);
                } catch (error) {
                    console.error('Error fetching cloud kitchens:', error);
                } finally {
                    setLoading(false);
                }
            };
            loadCloudKitchens();
        }
    }, [location.state]);

    // --- 2. Filter Logic ---
    const handleCategory = (cat) => {
        setActiveCat(cat);
        setSearchTerm(''); 
        let arr = cloudKitchens;
        if (cat !== 'All') {
            arr = arr.filter(k => {
                const lower = cat.toLowerCase();
                return (
                    (k.name && k.name.toLowerCase().includes(lower)) ||
                    (k.cuisine && k.cuisine.toLowerCase().includes(lower)) ||
                    (k.category && k.category.toLowerCase().includes(lower)) ||
                    (k.type && k.type.toLowerCase().includes(lower)) ||
                    (Array.isArray(k.items) && k.items.some(item =>
                        (item.name && item.name.toLowerCase().includes(lower)) ||
                        (item.category && item.category.toLowerCase().includes(lower))
                    ))
                );
            });
        }
        setFiltered(arr);
    };

    // --- 3. Search Logic ---
    const handleSearch = (e) => {
        e.preventDefault();
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            handleCategory(activeCat);
            return;
        }
        setActiveCat('All');
        setFiltered(
            cloudKitchens.filter(k =>
                (k.name && k.name.toLowerCase().includes(term)) ||
                (k.cuisine && k.cuisine.toLowerCase().includes(term)) ||
                (k.location?.street && k.location.street.toLowerCase().includes(term)) ||
                (k.location?.city && k.location.city.toLowerCase().includes(term)) ||
                (k.location?.state && k.location.state.toLowerCase().includes(term)) ||
                (k.location?.country && k.location.country.toLowerCase().includes(term)) ||
                (Array.isArray(k.items) && k.items.some(item =>
                    (item.name && item.name.toLowerCase().includes(term)) ||
                    (item.category && item.category.toLowerCase().includes(term)) ||
                    (item.type && item.type.toLowerCase().includes(term))
                ))
            )
        );
    };

    // --- 4. Scroll Logic ---
    const scrollCategory = (direction) => {
        const container = scrollRef.current;
        if (!container) return;
        const scrollAmount = 250;
        container.scrollBy({ left: direction === 'right' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
    };

    const handleScroll = () => {
        const container = scrollRef.current;
        if (!container) return;
        const tolerance = 2;
        setShowLeftArrow(container.scrollLeft > 10);
        const isAtEnd = (container.scrollLeft + container.clientWidth) >= (container.scrollWidth - tolerance);
        setShowRightArrow(!isAtEnd);
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (container) {
            handleScroll();
            container.addEventListener('scroll', handleScroll);
            window.addEventListener('resize', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            };
        }
    }, []);

    const sortedFiltered = [...filtered].sort((a, b) => {
        const aOpen = isCloudKitchenOpen(
    a.operatingHours || a.timings,
    a.location?.country
);
        const bOpen = isCloudKitchenOpen(
            b.operatingHours || b.timings,
            b.location?.country
        );
        if (aOpen === bOpen) return 0;
        return aOpen ? -1 : 1;
    });


    if (loading) {
        return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
    }

    return (
        <Container className="cloud-kitchens-container">
            
            {/* Browsing Popup (Structure Updated) */}
            <AnimatePresence>
                {showPopup && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="browsing-popup"
                    >
                        <div className="popup-content">
                            <span className="popup-icon">🍳</span>
                            <span>You are browsing <b>Cloud Kitchens</b></span>
                        </div>
                        <button
                            onClick={() => setShowPopup(false)}
                            className="close-popup-btn"
                            aria-label="Close"
                        >
                            <FaTimesCircle />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Header Section */}
            <div className="cloud-header">
                <button
                    onClick={() => navigate(-1)}
                    className="back-btn"
                    aria-label="Go back"
                    type="button"
                >
                    <FaChevronLeft />
                </button>
                <h2 className="page-title">Cloud Kitchens</h2>
            </div>

            {/* Modern Search Bar */}
            <form onSubmit={handleSearch} className="search-form">
                <div className="search-wrapper">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by name, cuisine, location..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>
            </form>

            {/* Category Scroller */}
            <div className="category-wrapper">
                {showLeftArrow && (
                    <button 
                        onClick={() => scrollCategory('left')} 
                        className="scroll-arrow left"
                        aria-label="Scroll left"
                    >
                        <FaChevronLeft size={14} />
                    </button>
                )}
                
                <div ref={scrollRef} className="category-list">
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleCategory('All')}
                        className={`cat-pill all`}
                        style={{
                            background: activeCat === 'All' ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : '#eee',
                            color: activeCat === 'All' ? '#fff' : '#222',
                            boxShadow: activeCat === 'All' ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                        }}
                    >
                        🍽️ All
                    </motion.div>

                    {cloudKitchenCategories.map((cat, i) => (
                        <motion.div
                            key={i}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleCategory(cat.alt)}
                            className="cat-pill item"
                            style={{
                                background: activeCat === cat.alt ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)' : '#fff',
                                boxShadow: activeCat === cat.alt ? '0 4px 16px rgba(229,46,113,0.13)' : 'none',
                                border: activeCat === cat.alt ? '2px solid #e52e71' : '1px solid #eee',
                            }}
                        >
                            <img 
                                src={cat.src} 
                                alt={cat.alt} 
                                className="cat-img"
                                style={{ border: activeCat === cat.alt ? '2.5px solid #e52e71' : '2px solid #eee' }}
                            />
                            <div className="cat-name" style={{ color: activeCat === cat.alt ? '#e52e71' : '#222' }}>
                                {cat.alt}
                            </div>
                        </motion.div>
                    ))}
                </div>

                {showRightArrow && (
                    <button 
                        onClick={() => scrollCategory('right')} 
                        className="scroll-arrow right"
                        aria-label="Scroll right"
                    >
                        <FaChevronRight size={14} />
                    </button>
                )}
            </div>

            {/* Grid System */}
            <div ref={listRef}>
                {sortedFiltered.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#888', fontSize: '1.2rem', padding: '3rem 0', width: '100%' }}>
                        No cloud kitchens found for this category or search.
                    </div>
                ) : (
                    <Row className="g-4">
                        {sortedFiltered.map(k => {
                            let cuisines = [];
                            if (Array.isArray(k.cuisine)) {
                                cuisines = k.cuisine;
                            } else if (typeof k.cuisine === 'string') {
                                cuisines = k.cuisine.split(',').map(c => c.trim()).filter(Boolean);
                            } else if (k.category) {
                                cuisines = [k.category];
                            }
                            const cuisineDisplay = cuisines.slice(0, 2).join(', ');
                            const moreCuisines = cuisines.length > 2;
                            const open = isCloudKitchenOpen(
                                k.operatingHours || k.timings,
                                k.location?.country
                            );

                            return (
                                <Col key={k._id} xs={12} md={6} lg={4} xl={3}>
                                    <motion.div whileHover={{ y: -5 }} className="kitchen-card">
                                        <Link to={`/cloudkitchens/${k.slug || k._id}`} className="card-link">
                                            {k.deliveryTime && (
                                                <div className="badge-delivery">
                                                    <svg width="14" height="14" fill="none" stroke="#e05260" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                                    {k.deliveryTime}
                                                </div>
                                            )}
                                            <div className={`badge-status ${open ? 'open' : 'closed'}`}>
                                                {open ? 'Opened' : 'Closed'}
                                            </div>
                                            <img src={k.image || 'https://via.placeholder.com/240'} alt={k.name} className="card-image" />
                                            <div className="card-name" title={k.name}>{k.name}</div>
                                            <div className="card-cuisine">
                                                {cuisineDisplay}
                                                {moreCuisines && <span style={{ opacity: 0.7, marginLeft: 4 }}>+ more</span>}
                                                {!cuisineDisplay && (k.category || 'Multi-cuisine')}
                                            </div>
                                            <div className="spacer" />
                                            <motion.button
                                                whileTap={{ scale: 0.95 }}
                                                className="btn-view-menu"
                                                style={{
                                                    background: open ? 'linear-gradient(90deg, #ff8a00 0%, #e52e71 100%)' : '#ccc',
                                                    cursor: open ? 'pointer' : 'not-allowed',
                                                    opacity: open ? 1 : 0.7
                                                }}
                                                tabIndex={-1}
                                                disabled={!open}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    if (open) navigate(`/cloudkitchens/${k.slug || k._id}`);
                                                }}
                                            >
                                                {open ? 'See Menu' : 'Closed'}
                                            </motion.button>
                                        </Link>
                                    </motion.div>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </div>
        </Container>
    );
};

export default CloudKitchens;